<template>
  <admin-layout :title="$t('edit_x', { x: $t('Role') })">
    <div>
      <div class="">
        <tec-form-section @submitted="update">
          <template #title>
            <div class="flex items-center">
              <Link class="text-indigo-400 hover:text-indigo-600" :href="route('roles')">{{ $t('Roles') }}</Link>
              <span class="text-indigo-400 font-medium mx-2">/</span>
              <img v-if="edit.photo" class="block w-6 h-6 rounded-full mr-2" :src="edit.photo" />
              {{ edit.name }}
            </div>
          </template>

          <template #description>{{ $t('Update the record by modifying the details in the form below') }}</template>

          <template #form>
            <div class="col-span-full lg:col-span-4">
              <trashed-message v-if="edit.deleted_at" class="mb-6" @restore="restore">
                {{ $t('This record has been deleted.') }}
              </trashed-message>
              <text-input
                :label="$t('Name')"
                v-model="form.name"
                :error="$page.props.errors.name ? $page.props.errors.name.replace('{attribute}', 'name') : null"
              />
            </div>
          </template>

          <template #actions>
            <div class="w-full flex items-center justify-between">
              <button
                type="button"
                @click="destroy"
                v-if="!edit.deleted_at"
                class="text-red-600 px-4 py-2 rounded border-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-gray-300"
              >
                {{ $t('delete_x', { x: $t('Role') }) }}
              </button>
              <button
                v-else
                type="button"
                @click="deletePermanently"
                class="text-red-600 px-4 py-2 rounded border-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-gray-300"
              >
                {{ $t('delete_x', { x: $t('Permanently') }) }}
              </button>
              <div class="flex items-center">
                <tec-action-message :on="form.recentlySuccessful" class="mr-3">{{ $t('Saved.') }} </tec-action-message>
                <loading-button type="submit" :loading="form.processing" :disabled="form.processing">{{ $t('Save') }} </loading-button>
              </div>
            </div>
          </template>
        </tec-form-section>
      </div>

      <!-- Delete User Confirmation Modal -->
      <Dialog
        max-width="md"
        :show="permanent"
        action-type="delete"
        title="Delete Role?"
        :close="closePermanentModal"
        :action="deleteRolePermanently"
        action-text="Delete Permanently"
        :content="`<p class='mb-2'>${$t('Are you sure you want to delete the record permanently?')}</p>
        <p>${$t('Once deleted, all of its resources and data will be permanently deleted.')}</p>`"
      />

      <!-- Delete Account Confirmation Modal -->
      <Dialog
        :show="confirm"
        :close="closeModal"
        :action="deleteRole"
        action-type="delete"
        title="Delete Role?"
        action-text="Delete Role"
        :content="$t('Are you sure you want to delete the record?')"
      />

      <!-- Restore Account Confirmation Modal -->
      <Dialog
        :show="restoreConf"
        :action="restoreRole"
        title="Restore Role!"
        :close="closeRestoreModal"
        action-text="Restore Role"
        :content="$t('Are you sure you want to restore the record?')"
      />

      <!-- Role Permissions Form -->
      <div class="mt-8">
        <tec-form-section @submitted="submit">
          <template #title>{{ $t('Role Permissions') }}</template>
          <template #description>{{ $t('Update the role permissions in the form below') }}</template>

          <template #form>
            <div class="col-span-full">
              <div class="flex flex-wrap items-center gap-x-6 gap-y-3">
                <label class="block w-full font-medium text-sm text-gray-700">{{ $t('Sales') }}</label>
                <check-box
                  id="read-sales"
                  value="read-sales"
                  @input="updatePermissions('read-sales')"
                  :label="$t('view_x', { x: $t('My Sales') })"
                  :checked="permissions.includes('read-sales')"
                />
                <check-box
                  id="read-all-sales"
                  value="read-all-sales"
                  @input="updatePermissions('read-all-sales')"
                  :label="$t('view_x', { x: $t('All Sales') })"
                  :checked="permissions.includes('read-all-sales')"
                />
                <check-box
                  id="show-details"
                  value="show-details"
                  @input="updatePermissions('show-details')"
                  :label="$t('Show all columns')"
                  :checked="permissions.includes('show-details')"
                />
                <check-box
                  id="create-sales"
                  value="create-sales"
                  @input="updatePermissions('create-sales')"
                  :label="$t('create_x', { x: $t('Sales') })"
                  :checked="permissions.includes('create-sales')"
                />
                <check-box
                  id="update-sales"
                  value="update-sales"
                  @input="updatePermissions('update-sales')"
                  :label="$t('update_x', { x: $t('Sales') })"
                  :checked="permissions.includes('update-sales')"
                />
                <check-box
                  id="delete-sales"
                  value="delete-sales"
                  @input="updatePermissions('delete-sales')"
                  :label="$t('delete_x', { x: $t('Sales') })"
                  :checked="permissions.includes('delete-sales')"
                />
              </div>

              <div class="flex flex-wrap items-center gap-x-6 gap-y-3 mt-8">
                <label class="block w-full font-medium text-sm text-gray-700">{{ $t('Customers') }}</label>
                <check-box
                  id="read-customers"
                  value="read-customers"
                  @input="updatePermissions('read-customers')"
                  :label="$t('view_x', { x: $t('Customers') })"
                  :checked="permissions.includes('read-customers')"
                />
                <check-box
                  id="create-customers"
                  value="create-customers"
                  @input="updatePermissions('create-customers')"
                  :label="$t('create_x', { x: $t('Customers') })"
                  :checked="permissions.includes('create-customers')"
                />
                <check-box
                  id="update-customers"
                  value="update-customers"
                  @input="updatePermissions('update-customers')"
                  :label="$t('update_x', { x: $t('Customers') })"
                  :checked="permissions.includes('update-customers')"
                />
                <check-box
                  id="delete-customers"
                  value="delete-customers"
                  @input="updatePermissions('delete-customers')"
                  :label="$t('delete_x', { x: $t('Customers') })"
                  :checked="permissions.includes('delete-customers')"
                />
              </div>

              <!-- <div class="flex flex-wrap items-center gap-x-6 gap-y-3 mt-8">
                <label class="block w-full font-medium text-sm text-gray-700">{{ $t('Tax Rates') }}</label>
                <check-box
                  id="read-tax-rates"
                  value="read-tax-rates"
                  @input="updatePermissions('read-tax-rates')"
                  :label="$t('view_x', { x: $t('Tax Rates') })"
                  :checked="permissions.includes('read-tax-rates')"
                />
                <check-box
                  id="create-tax-rates"
                  value="create-tax-rates"
                  @input="updatePermissions('create-tax-rates')"
                  :label="$t('create_x', { x: $t('Tax Rates') })"
                  :checked="permissions.includes('create-tax-rates')"
                />
                <check-box
                  id="update-tax-rates"
                  value="update-tax-rates"
                  @input="updatePermissions('update-tax-rates')"
                  :label="$t('update_x', { x: $t('Tax Rates') })"
                  :checked="permissions.includes('update-tax-rates')"
                />
                <check-box
                  id="delete-tax-rates"
                  value="delete-tax-rates"
                  @input="updatePermissions('delete-tax-rates')"
                  :label="$t('delete_x', { x: $t('Tax Rates') })"
                  :checked="permissions.includes('delete-tax-rates')"
                />
              </div> -->

              <div class="flex flex-wrap items-center gap-x-6 gap-y-3 mt-8">
                <label class="block w-full font-medium text-sm text-gray-700">{{ $t('Users') }}</label>
                <check-box
                  id="read-users"
                  value="read-users"
                  @input="updatePermissions('read-users')"
                  :label="$t('view_x', { x: $t('Users') })"
                  :checked="permissions.includes('read-users')"
                />
                <check-box
                  id="create-users"
                  value="create-users"
                  @input="updatePermissions('create-users')"
                  :label="$t('create_x', { x: $t('Users') })"
                  :checked="permissions.includes('create-users')"
                />
                <check-box
                  id="update-users"
                  value="update-users"
                  @input="updatePermissions('update-users')"
                  :label="$t('update_x', { x: $t('Users') })"
                  :checked="permissions.includes('update-users')"
                />
                <check-box
                  id="delete-users"
                  value="delete-users"
                  @input="updatePermissions('delete-users')"
                  :label="$t('delete_x', { x: $t('Users') })"
                  :checked="permissions.includes('delete-users')"
                />
              </div>

              <div class="flex flex-wrap items-center gap-x-6 gap-y-3 mt-8">
                <label class="block w-full font-medium text-sm text-gray-700">{{ $t('Roles') }}</label>
                <check-box
                  id="read-roles"
                  value="read-roles"
                  @input="updatePermissions('read-roles')"
                  :label="$t('view_x', { x: $t('Roles') })"
                  :checked="permissions.includes('read-roles')"
                />
                <check-box
                  id="create-roles"
                  value="create-roles"
                  @input="updatePermissions('create-roles')"
                  :label="$t('create_x', { x: $t('Roles') })"
                  :checked="permissions.includes('create-roles')"
                />
                <check-box
                  id="update-roles"
                  value="update-roles"
                  @input="updatePermissions('update-roles')"
                  :label="$t('update_x', { x: $t('Roles') })"
                  :checked="permissions.includes('update-roles')"
                />
                <check-box
                  id="delete-roles"
                  value="delete-roles"
                  @input="updatePermissions('delete-roles')"
                  :label="$t('delete_x', { x: $t('Roles') })"
                  :checked="permissions.includes('delete-roles')"
                />
              </div>

              <div class="flex flex-wrap items-center gap-x-6 gap-y-3 mt-8">
                <label class="block w-full font-medium text-sm text-gray-700">{{ $t('Misc') }}</label>
                <check-box
                  id="read-activity"
                  value="read-activity"
                  @input="updatePermissions('read-activity')"
                  :label="$t('view_x', { x: $t('Activity') })"
                  :checked="permissions.includes('read-activity')"
                />
              </div>
            </div>
          </template>

          <template #actions>
            <div class="w-full flex items-center justify-end">
              <div class="flex items-center">
                <tec-action-message :on="pform.recentlySuccessful" class="mr-3">{{ $t('Saved.') }}</tec-action-message>
                <loading-button type="submit" :loading="pform.processing" :disabled="pform.processing">{{ $t('Save') }}</loading-button>
              </div>
            </div>
          </template>
        </tec-form-section>
      </div>
    </div>
  </admin-layout>
</template>

<script>
import { Link } from '@inertiajs/inertia-vue3';

import Dialog from '@/Shared/Dialog';
import CheckBox from '@/Shared/CheckBox';
import TextInput from '@/Shared/TextInput';
import AdminLayout from '@/Layouts/AdminLayout';
import LoadingButton from '@/Shared/LoadingButton';
import TecDialogModal from '@/Jetstream/DialogModal';
import TrashedMessage from '@/Shared/TrashedMessage';
import TecFormSection from '@/Jetstream/FormSection';
import TecSectionTitle from '@/Jetstream/SectionTitle';
import TecActionMessage from '@/Jetstream/ActionMessage';

export default {
  props: ['edit'],

  components: {
    Link,
    Dialog,
    CheckBox,
    TextInput,
    AdminLayout,
    LoadingButton,
    TecDialogModal,
    TecFormSection,
    TrashedMessage,
    TecSectionTitle,
    TecActionMessage,
  },

  data() {
    return {
      confirm: false,
      permissions: [],
      permanent: false,
      restoreConf: false,
      form: this.$inertia.form({
        _method: 'put',
        photo: null,
        password: null,
        name: this.edit.name,
        email: this.edit.email,
        password_confirmation: null,
        rolename: this.edit.rolename,
      }),
      pform: this.$inertia.form({
        _method: 'post',
        permissions: this.edit.permissions,
      }),
    };
  },

  mounted() {
    this.permissions = this.edit.permissions;
    delete this.edit.permissions;
  },

  methods: {
    update() {
      this.form.post(this.route('roles.update', this.edit.id), {
        preserveScroll: true,
        onSuccess: () => this.form.reset('password', 'photo'),
      });
    },
    updatePermissions(v) {
      if (this.permissions.includes(v)) {
        this.permissions = this.permissions.filter(r => r != v);
      } else {
        this.permissions = [...this.permissions, v];
      }
    },
    submit() {
      this.pform
        .transform(data => ({ ...data, permissions: this.permissions }))
        .post(this.route('roles.permissions', this.edit.id), { preserveScroll: true });
    },
    destroy() {
      this.confirm = true;
    },
    deleteRole() {
      this.form.delete(route('roles.destroy', this.edit.id), {
        onSuccess: () => this.closeModal(),
      });
    },
    closeModal() {
      this.confirm = false;
    },
    restore() {
      this.restoreConf = true;
    },
    restoreRole() {
      this.$inertia.put(this.route('roles.restore', this.edit.id), {
        onSuccess: () => (this.restoreConf = false),
      });
    },
    closeRestoreModal() {
      this.restoreConf = false;
    },
    deletePermanently() {
      this.permanent = true;
    },
    deleteRolePermanently() {
      this.form.delete(route('roles.destroy.permanently', this.edit.id), {
        onSuccess: () => this.closeModal(),
      });
    },
    closePermanentModal() {
      this.permanent = false;
    },
  },
};
</script>
